import React, { useState } from "react";
import {
  Table,
  Tag,
  Icon,
  Button,
  InputNumber,
  DatePicker,
  Modal,
  notification,
} from "antd";
import moment from "moment";
import api from "../../../../../services/api";
import * as DateFormater from "../../../../../utils/DateFormater";
import * as MoneyFormater from "../../../../../utils/MoneyFormater";
import { NotaFiscalParcela } from "../../../../Acordo/View/AcordoView/AcordoParcelas/Parcela/NotaFiscal";
import { StatusHO } from "../../../../Acordo/View/AcordoView/AcordoParcelas/Parcela/StatusHO";
import { ValorHO } from "../../../../Acordo/View/AcordoView/AcordoParcelas/Parcela/ValorHO";
import "./styles.css";

const AcordoParcelas = ({ parcelas, acordoId, onUpdate }) => {
  const [editingParcelaId, setEditingParcelaId] = useState(null);
  const [editValor, setEditValor] = useState(0);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!parcelas || parcelas.length === 0) {
    return (
      <div className="acordo-parcelas__empty">
        <p>Nenhuma parcela cadastrada</p>
      </div>
    );
  }

  const handleReceber = async (parcela) => {
    if (!editValor || Number(editValor) < 0.01) {
      Modal.error({ title: "Erro", content: "Valor inválido." });
      return;
    }
    if (!editData) {
      Modal.error({ title: "Erro", content: "Data de pagamento inválida." });
      return;
    }

    setLoading(true);

    try {
      await api.put(`/acordo/parcela/${parcela.id}/`, {
        status_parcela: "1",
        valor_pago: editValor,
        data_pagamento: editData,
        data_vencimento: parcela.data_vencimento,
        num_parcela: parcela.num_parcela,
      });

      notification.success({ message: "Parcela recebida." });
      setEditingParcelaId(null);
      setEditValor(0);
      setEditData(null);
      if (onUpdate) onUpdate();
    } catch (err) {
      if (err.response && err.response.status === 403) {
        Modal.error({ title: "Erro", content: "Sem permissão." });
      } else {
        Modal.error({
          title: "Erro",
          content: "Não foi possível atualizar a parcela.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDespagar = async (parcela) => {
    Modal.confirm({
      title: "Despagar parcela",
      content: "Deseja cancelar o pagamento dessa parcela?",
      okText: "Confirmar",
      cancelText: "Cancelar",
      onOk: async () => {
        setLoading(true);
        try {
          await api.put(`/acordo/parcela/${parcela.id}/`, {
            status_parcela: "0",
            data_pagamento: null,
            valor_pago: null,
            data_vencimento: parcela.data_vencimento,
            num_parcela: parcela.num_parcela,
          });

          notification.success({
            message: "Pagamento da parcela cancelado.",
          });
          if (onUpdate) onUpdate();
        } catch (err) {
          if (err.response && err.response.status === 403) {
            Modal.error({ title: "Erro", content: "Sem permissão." });
          } else {
            Modal.error({
              title: "Erro",
              content: "Não foi possível atualizar a parcela.",
            });
          }
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleStartEdit = (parcela) => {
    setEditingParcelaId(parcela.id);
    setEditValor(parcela.valor_parcela || 0);
    setEditData(moment().format("YYYY-MM-DD"));
  };

  const handleCancelEdit = () => {
    setEditingParcelaId(null);
    setEditValor(0);
    setEditData(null);
  };

  const columns = [
    {
      title: "Nº",
      dataIndex: "num_parcela",
      key: "num_parcela",
      width: 60,
      render: (num) => `${num}º`,
    },
    {
      title: "Valor",
      dataIndex: "valor_parcela",
      key: "valor_parcela",
      width: 120,
      align: "right",
      render: (value) =>
        value !== null && value !== undefined
          ? `R$ ${MoneyFormater.format(value)}`
          : "-",
    },
    {
      title: "Vencimento",
      dataIndex: "data_vencimento",
      key: "data_vencimento",
      width: 120,
      render: (data) => (data ? DateFormater.format(data) : "-"),
    },
    {
      title: "Status",
      dataIndex: "status_parcela",
      key: "status_parcela",
      width: 100,
      render: (status) => {
        const statusMap = {
          0: { text: "Pendente", color: "orange" },
          1: { text: "Paga", color: "green" },
          2: { text: "Cancelada", color: "red" },
          3: { text: "Conciliada", color: "blue" },
        };
        const statusInfo = statusMap[status] || {
          text: "N/A",
          color: "default",
        };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: "Valor Pago",
      dataIndex: "valor_pago",
      key: "valor_pago",
      width: 150,
      align: "right",
      render: (value, record) => {
        if (editingParcelaId === record.id) {
          return (
            <InputNumber
              value={editValor}
              onChange={setEditValor}
              formatter={(val) =>
                `R$ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(val) => val.replace(/R\$\s?|(,*)/g, "")}
              precision={2}
              style={{ width: "100%" }}
            />
          );
        }
        return value !== null && value !== undefined
          ? `R$ ${MoneyFormater.format(value)}`
          : "-";
      },
    },
    {
      title: "Data Pagamento",
      dataIndex: "data_pagamento",
      key: "data_pagamento",
      width: 150,
      render: (data, record) => {
        if (editingParcelaId === record.id) {
          return (
            <DatePicker
              value={editData ? moment(editData, "YYYY-MM-DD") : null}
              onChange={(date) =>
                setEditData(date ? date.format("YYYY-MM-DD") : null)
              }
              format="DD/MM/YYYY"
              style={{ width: "100%" }}
            />
          );
        }
        return data ? DateFormater.format(data) : "-";
      },
    },
    {
      title: "Nota Fiscal",
      dataIndex: "numero_nf",
      key: "numero_nf",
      width: 400,
      render: (_, record) => (
        <NotaFiscalParcela
          idParcela={record.id}
          initialValue={record.numero_nf}
          dataNF={record.data_nf}
          data={record}
        />
      ),
    },
    {
      title: "Status HO",
      dataIndex: "status_ho_contratual",
      key: "status_ho_contratual",
      width: 180,
      render: (value, record) => (
        <StatusHO
          data={record}
          idParcela={record.id}
          status={value || record.status_ho_contratual || "-"}
        />
      ),
    },
    {
      title: "Valor HO",
      dataIndex: "valor_ho_contratual",
      key: "valor_ho_contratual",
      width: 160,
      align: "right",
      render: (value, record) => (
        <ValorHO
          data={record}
          idParcela={record.id}
          valor={value !== null && value !== undefined ? value : 0}
        />
      ),
    },
    {
      title: "Forma Pagamento",
      dataIndex: "forma_pagamento",
      key: "forma_pagamento",
      width: 150,
      render: (forma) => forma || "-",
    },
    {
      title: "Operador",
      dataIndex: "operador_nome",
      key: "operador_nome",
      width: 160,
      render: (operador, record) => {
        const operadorNome = operador || record.operador || record.operador_id;
        return operadorNome || "---";
      },
    },
    {
      title: "Ações",
      key: "acoes",
      width: 180,
      fixed: "right",
      render: (_, record) => {
        const isPending = record.status_parcela === "0";
        const isPaid = record.status_parcela === "1";
        const isEditing = editingParcelaId === record.id;

        if (isEditing) {
          return (
            <div style={{ display: "flex", gap: 4 }}>
              <Button
                size="small"
                type="primary"
                icon="check"
                loading={loading}
                onClick={() => handleReceber(record)}
              >
                Confirmar
              </Button>
              <Button
                size="small"
                icon="close"
                disabled={loading}
                onClick={handleCancelEdit}
              >
                Cancelar
              </Button>
            </div>
          );
        }

        if (isPending) {
          return (
            <Button
              size="small"
              type="primary"
              ghost
              icon="dollar"
              onClick={() => handleStartEdit(record)}
            >
              RECEBER
            </Button>
          );
        }

        if (isPaid) {
          return (
            <Button
              size="small"
              className="btn-green btn-green-ghost"
              ghost
              icon="check-circle"
              onClick={() => handleDespagar(record)}
              loading={loading}
            >
              RECEBIDO
            </Button>
          );
        }

        return (
          <Button size="small" type="danger" ghost icon="close-circle" disabled>
            CANCELADO
          </Button>
        );
      },
    },
  ];

  const getRowClassName = (record) => {
    if (record.status_parcela === "1") {
      return "acordo-parcelas__row--pago";
    }
    if (record.status_parcela === "0" && record.data_vencimento) {
      const hoje = new Date();
      const vencimento = new Date(record.data_vencimento);
      if (vencimento < hoje) {
        return "acordo-parcelas__row--vencido";
      }
    }
    return "";
  };

  return (
    <div className="acordo-parcelas__container">
      <div className="acordo-parcelas__header">
        <Icon type="ordered-list" />
        <h4>Parcelas do Acordo</h4>
      </div>

      <Table
        columns={columns}
        dataSource={parcelas}
        rowKey="id"
        size="small"
        scroll={{ x: 2000 }}
        pagination={false}
        rowClassName={getRowClassName}
        className="acordo-parcelas__table"
      />
    </div>
  );
};

export default AcordoParcelas;



// WEBPACK FOOTER //
// ./src/pages/Pasta/Acordos/AcordoView/AcordoParcelas/index.jsx